import { useNavigate, useSearch } from '@tanstack/react-router'
import { useState } from 'react'

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Link,
  SpreadsheetBackground,
} from '@/components/ui'
import { AuthForm, OtpForm, useAuthMutations } from '@/modules/auth'
import { SignInAccountNotFoundError } from '@/modules/errors'
import { getCourtoisPrivacyUrl, getCourtoisTermsUrl } from '@/services/courtois'

type LoginState = 'email' | 'not-found' | 'otp'

export const LoginPage = () => {
  const navigate = useNavigate()
  const { redirect } = useSearch({ from: '/login' })
  const [email, setEmail] = useState('')
  const [state, setState] = useState<LoginState>('email')
  const { signIn } = useAuthMutations()
  const signUpPath = redirect?.startsWith('/invitations')
    ? '/user-sign-up'
    : '/sign-up'

  const handleEmailSubmit = (email: string) => {
    setEmail(email)
    return signIn({ email })
      .then(() => setState('otp'))
      .catch((e) => {
        const isUnknownError = !(e instanceof SignInAccountNotFoundError)
        if (isUnknownError) throw e
        setState('not-found')
      })
  }

  const navigateAfterLogin = () => {
    navigate({ to: redirect ?? '/' })
  }

  const handleDifferentEmail = () => {
    setState('email')
    setEmail('')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 relative overflow-hidden">
      <SpreadsheetBackground />
      <div className="w-full max-w-105 relative z-10">
        <div className="flex flex-col items-center">
          <div className="mb-10">
            <img src="/logo.svg" alt="CounselPro" className="h-10 w-auto" />
          </div>

          <Card className="w-full">
            {state === 'email' && (
              <AuthForm
                onSubmit={handleEmailSubmit}
                onSignedIn={navigateAfterLogin}
                title="Sign in to your account"
                description="Enter your email to receive a login code"
                footerTitle="New user"
                footerLabel="Don't have an account?"
                footerLink={{ to: signUpPath, label: 'Create account' }}
              />
            )}

            {state === 'not-found' && (
              <>
                <CardHeader className="space-y-1 pb-4">
                  <h1 className="text-2xl font-semibold tracking-tight text-center">
                    Account not found
                  </h1>
                  <p className="text-sm text-muted-foreground text-center">
                    No account exists for{' '}
                    <span className="font-medium text-foreground">{email}</span>
                  </p>
                </CardHeader>

                <CardContent className="space-y-4">
                  <p className="text-sm text-center text-muted-foreground">
                    Sign up to continue
                  </p>
                  <Button className="w-full h-10 font-medium" asChild>
                    <Link to="/sign-up">Create Account</Link>
                  </Button>
                  <Button
                    onClick={handleDifferentEmail}
                    theme="ghost"
                    className="w-full h-10 font-medium"
                  >
                    Use different email
                  </Button>
                </CardContent>
              </>
            )}

            {state === 'otp' && (
              <OtpForm
                email={email}
                onCodeVerified={navigateAfterLogin}
                onReturn={handleDifferentEmail}
                onResend={() => signIn({ email })}
                isLogin
              />
            )}
          </Card>

          {state === 'email' && (
            <p className="mt-6 text-center text-sm text-muted-foreground">
              No account yet?{' '}
              <Link to="/trial" theme="brand" className="font-medium">
                Start a free trial
              </Link>
            </p>
          )}

          <p className="mt-8 text-center text-xs text-muted-foreground max-w-sm">
            By continuing, you agree to our{' '}
            <Link href={getCourtoisTermsUrl()} size="xs" newTab>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link href={getCourtoisPrivacyUrl()} size="xs" newTab>
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  )
}
